import { STORE_SCREEN } from 'utils/storeScreen';
import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';
import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { AdVariant } from 'types/adEditorTypes';
import { toSavableVariant } from 'utils/adVariantSaveHelpers';
import { SCREEN_NAME } from 'utils/screenName';

/**
 * The ad builder's foundation for storing itself (steps A and B; Steven's
 * go-ahead of 4 Oct 2026).
 *
 * The ad builder is not like the job page. Its ads change on screen without
 * anyone editing: clicking another ad takes a new picture of the drawing, a
 * drawn picture arrives from the server, colours are worked out. Compared
 * whole, the screen would look changed for ever and store for ever. So only
 * what a PERSON changes is compared (step B), and the server's answer names
 * what it stored, so the screen never has to load every ad again after a
 * save (step A).
 */

/**
 * What a person changes on an ad. Everything else on an ad is either the
 * screen's own bookkeeping (`uuid`, `isDirty`, `data_url`, `config`) or
 * comes from the server (`resolved_*`, the picture fields, `id`).
 *
 * `svg` is the drawing itself: it changes when the person moves or edits
 * something in it, and also as a consequence of a choice of theirs (another
 * picture, another design). Both are their change.
 */
const COMPARED_FIELDS = [
  'function',
  'company',
  'creative_type',
  'placement',
  'headline',
  'text',
  'description',
  'svg',
  'cta',
  'display_link',
  'crop',
  'overlay_id',
  'template',
  'accent_color',
  'free_text',
  'subtext',
  'salary',
  'benefits',
  'banner_title',
  'primary_color',
  'secondary_color',
  'tertiary_color',
] as const;

/** The compared fields that change how the ad LOOKS (AB-6: redraw only then). */
const VISUAL_FIELDS = [
  'function',
  'company',
  'creative_type',
  'placement',
  'svg',
  'cta',
  'crop',
  'overlay_id',
  'template',
  'accent_color',
  'free_text',
  'subtext',
  'salary',
  'benefits',
  'banner_title',
  'primary_color',
  'secondary_color',
  'tertiary_color',
] as const;

/**
 * What Undo puts back on an ad: what the person made of it, with the picture
 * and logo themselves, and what the drawing is loaded from (`config`).
 * Never the ad's number or what the server says about its pictures: those
 * are facts of now. And never the small picture of the drawing
 * (`data_url`): it arrives a moment after the drawing changes, so the one a
 * step holds is the picture of the state BEFORE it (review of this slice).
 * The ad that changed is opened instead, which takes its picture again.
 */
export const RESTORED_FIELDS: readonly (keyof AdVariant)[] = [
  ...COMPARED_FIELDS,
  'path',
  'logo',
  'config',
];

/**
 * Undo/Redo: puts an ad back as it was in an earlier step, on `target`.
 * With the colours the server had worked out for that step: the colour
 * boxes show those first, so without them a box kept the newer colour after
 * Undo (Mark's QA test, 2 Nov 2026). The next drawing of the ad corrects
 * them if they moved since.
 */
export const restoreOnto = (target: AdVariant, was: AdVariant): void => {
  const lookBefore = adLookPrint(target);
  RESTORED_FIELDS.forEach((field) => {
    (target[field] as unknown) = was[field];
  });
  target.resolved_primary_color = was.resolved_primary_color;
  target.resolved_accent_color = was.resolved_accent_color;
  target.isDeleted = !!was.isDeleted;
  target.isDirty = true;
  // The small picture in hand shows the look that was just taken back.
  if (adLookPrint(target) !== lookBefore) target.data_url = undefined;
};

/**
 * Is this ad between two states? Right after another picture is chosen the
 * drawing is thrown away and built again a moment later. That moment is not
 * a state a person made, and must not become an undo step of its own.
 */
export const isBeingRedrawn = (variant: AdVariant): boolean =>
  !variant.isDeleted &&
  variant.path?.type === 'image' &&
  !variant.template &&
  !variant.svg;

const pick = (variant: AdVariant, fields: readonly (keyof AdVariant)[]) => {
  const out: Record<string, unknown> = {
    // A picture or a logo is the same one as long as its number is.
    path: variant.path?.id ?? null,
    logo: variant.logo?.id ?? null,
  };
  fields.forEach((field) => {
    const value = variant[field];
    // "nothing" is one thing, however it is spelled.
    out[field] = value === undefined || value === '' ? null : value;
  });
  return out;
};

/** What the person made of this ad, as one comparable text. */
export const adPrint = (variant: AdVariant): string =>
  JSON.stringify(pick(variant, COMPARED_FIELDS));

/** The same, for how the ad looks only. */
export const adLookPrint = (variant: AdVariant): string =>
  JSON.stringify(pick(variant, VISUAL_FIELDS));

/**
 * The whole screen as the shared saver compares it: every ad that is not
 * deleted, by the screen's own name for it, with what the person made of it.
 * A deleted ad drops out, which is itself a change.
 */
export const adsPrint = (variants: AdVariant[]): Record<string, string> => {
  const out: Record<string, string> = {};
  variants
    .filter((variant) => !variant.isDeleted)
    .forEach((variant) => {
      out[variant.uuid] = adPrint(variant);
    });
  return out;
};

/** What the server holds of each ad, as far as this screen knows. */
export interface StoredAds {
  /** The screen's name for the ad -> what was stored of it. */
  prints: Map<string, string>;
  looks: Map<string, string>;
}

export const rememberAsStored = (
  stored: StoredAds,
  variants: AdVariant[]
): void => {
  variants.forEach((variant) => {
    stored.prints.set(variant.uuid, adPrint(variant));
    stored.looks.set(variant.uuid, adLookPrint(variant));
  });
};

/**
 * What a save has to do, from what is on screen and what the server holds:
 * the ads to send (new ones, and those the person changed), and the ads to
 * delete (deleted on screen and known to the server by a number).
 */
export const whatToStore = (
  variants: AdVariant[],
  stored: StoredAds
): { toSend: AdVariant[]; toDelete: AdVariant[] } => ({
  toSend: variants.filter(
    (variant) =>
      !variant.isDeleted &&
      // New: no number, and (on a running campaign) no kept name either.
      ((!variant.id && !variant.kept_key) ||
        stored.prints.get(variant.uuid) !== adPrint(variant))
  ),
  toDelete: variants.filter((variant) => variant.isDeleted && !!variant.id),
});

/**
 * One ad as it travels. The same body the Save button sent, less what is the
 * screen's own bookkeeping, and with the picture of the drawing (`data_url`)
 * only when the LOOK changed: sent every time, it made the server treat
 * every save as a visual change and draw the ad's pictures again (AB-6).
 */
export const adForSending = (
  variant: AdVariant,
  stored: StoredAds,
  brandBannerTitle?: string | null
): Record<string, unknown> => {
  const body = { ...toSavableVariant(variant, brandBannerTitle) } as Record<
    string,
    unknown
  >;
  [
    'isDirty',
    'isDeleted',
    'path',
    'logo',
    'resolved_primary_color',
    'resolved_accent_color',
    'preview_url',
    'template_previews',
    'shipped_preview_url',
    'preview_pending',
    'preview_state',
    'preview_generation',
  ].forEach((field) => delete body[field]);

  const lookChanged =
    !variant.id || stored.looks.get(variant.uuid) !== adLookPrint(variant);
  if (!lookChanged) delete body.data_url;

  // The ad's name as it travels: unmistakable, so the server can tell a new
  // ad it already stored (its answer was lost) from another new ad.
  body.uuid = travelName(variant.uuid);

  return body;
};

/**
 * The screen's name for an ad is a short counter that starts again at every
 * page load. On the road it carries the name of this open screen in front,
 * so that it cannot repeat across visits.
 */
export const travelName = (uuid: string): string => `${SCREEN_NAME}:${uuid}`;

/** Back from the road: the screen's own name, or null when it is not ours. */
export const screenNameOf = (travelled: string | null): string | null =>
  travelled && travelled.startsWith(`${SCREEN_NAME}:`)
    ? travelled.slice(SCREEN_NAME.length + 1)
    : null;

/**
 * The body of a store the screen makes BY ITSELF. The mark tells the server
 * so: it refuses such a store on a published campaign, where every store
 * reaches the ad platforms within minutes.
 */
export const adsBody = (
  vacancyId: number,
  ads: Record<string, unknown>[],
  waits = false
): Record<string, unknown> => ({
  vacancy_id: vacancyId,
  kept_by_itself: true,
  // Which screen this store comes from (D10).
  store_screen: STORE_SCREEN,
  // The screen knows the campaign RUNS: what it sends is to be kept and to
  // wait for "Publish changes". Without this the server refuses the store
  // on a published campaign.
  ...(waits ? { knows_it_waits: true } : {}),
  adVariants: ads,
});

/** The server refused the screen's own store: the campaign is published. */
export class AdsArePublished extends Error {
  /** The campaign RUNS: the same work can be sent again as work to keep. */
  constructor(public readonly waits = false) {
    super('The campaign is published.');
    this.name = 'AdsArePublished';
  }
}

/**
 * The server refused ads FOR GOOD: sending the same again cannot help (a
 * web address in the wrong form, a text that is too long, a design outside
 * the plan). `byPlace` says which of the ads sent, by their place in the
 * request, when the server named them; empty when it did not.
 */
export class AdsRefused extends Error {
  constructor(
    public readonly reason: string,
    public readonly byPlace: Map<number, string>
  ) {
    super(reason);
    this.name = 'AdsRefused';
  }
}

/** Reads a refusal for good out of the server's answer. */
export const readRefusal = (answer: {
  message?: unknown;
  errors?: unknown;
}): AdsRefused => {
  const byPlace = new Map<number, string>();
  let first: string | undefined;
  const errors =
    answer.errors && typeof answer.errors === 'object'
      ? (answer.errors as Record<string, unknown>)
      : {};
  Object.entries(errors).forEach(([field, said]) => {
    const sentence = Array.isArray(said) ? said[0] : said;
    if (typeof sentence !== 'string' || !sentence) return;
    first = first ?? sentence;
    const place = /^adVariants\.(\d+)\./.exec(field);
    if (place && !byPlace.has(Number(place[1]))) {
      byPlace.set(Number(place[1]), sentence);
    }
  });
  const reason =
    first ??
    (typeof answer.message === 'string' && answer.message
      ? answer.message
      : 'the server refused this ad.');
  return new AdsRefused(reason, byPlace);
};

/** What the server says it stored of one ad (step A). */
export interface StoredAd {
  uuid: string | null;
  id: number | null;
  /** Running campaign: the name a new ad is kept under. */
  kept_key?: string | null;
  /** Running campaign: this ad is no longer there (removed by a publish). */
  gone?: boolean;
  preview_generation: number;
  preview_state: 'pending' | 'ready' | 'failed' | null;
  preview_pending: boolean;
}

const headers = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
  'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
});

const base = (): string => (import.meta.env.VITE_BASE_URL_API as string) ?? '';

const signedOut = (): never => {
  if (window.location.pathname !== '/') {
    leaveSignedOut();
  }
  throw new Error('The session has ended.');
};

/**
 * Stores ads, quietly (no toast: the corner says what is happening, and the
 * saver retries by itself), and answers with what the server stored.
 *
 * @throws when the server did not confirm the work
 */
export const saveAds = async (
  vacancyId: number,
  ads: Record<string, unknown>[],
  signal?: AbortSignal,
  waits = false,
  guard?: OutOfDateGuard
): Promise<StoredAd[]> => {
  const response = await fetch(`${base()}/v4/ad-variants`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify(
      withBasedOn(adsBody(vacancyId, ads, waits), guard?.basedOn)
    ),
    signal,
  });
  if (response.status === 401) signedOut();
  // D10: out of date is not a failure; the person chooses. (Reads a copy:
  // the campaign's own 409, "published meanwhile", is read below.)
  const version = await refusedOrVersion(response);
  if (response.status === 409) {
    const refused = (await response.json().catch(() => ({}))) as {
      published_meanwhile?: boolean;
      waits?: boolean;
    };
    if (refused.published_meanwhile) {
      throw new AdsArePublished(!!refused.waits);
    }
  }
  // Refused for good: the ad itself is at fault (422), or the plan does not
  // allow the channel (470). Trying the same again cannot help.
  if (response.status === 422 || response.status === 470) {
    throw readRefusal(
      (await response.json().catch(() => ({}))) as {
        message?: unknown;
        errors?: unknown;
      }
    );
  }
  if (!response.ok) {
    throw new Error(`The ads were not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
    data?: { adVariants?: StoredAd[] };
  };
  if (answer.success === false) throw new Error('The ads were not stored.');
  guard?.stored(version);

  return answer.data?.adVariants ?? [];
};

/** Deletes one stored ad. @throws when the server did not confirm it */
export const deleteAd = async (
  vacancyId: number,
  adId: number,
  signal?: AbortSignal
): Promise<void> => {
  const response = await fetch(`${base()}/v4/ad-variants/${adId}`, {
    method: 'DELETE',
    headers: headers(),
    // Marked as the screen's own: the server turns it away on a campaign
    // that was published meanwhile, where a delete pauses the ad on the ad
    // platforms at once.
    body: JSON.stringify({
      vacancy_id: vacancyId,
      kept_by_itself: true,
      store_screen: STORE_SCREEN,
    }),
    signal,
  });
  if (response.status === 401) signedOut();
  if (response.status === 409) {
    const refused = (await response.json().catch(() => ({}))) as {
      published_meanwhile?: boolean;
      waits?: boolean;
    };
    if (refused.published_meanwhile) {
      throw new AdsArePublished(!!refused.waits);
    }
  }
  // Already gone is as good as deleted: a retry after an answer that never
  // arrived must not fail for ever.
  if (!response.ok && response.status !== 404) {
    throw new Error(`The ad was not deleted (${response.status}).`);
  }
};
